import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, formatDistanceToNow } from 'date-fns';
import { Helmet } from 'react-helmet-async';

// Component CSS
import './Dashboard.css';
import './Profile.css';
import './MySession.css';
import './MyForm.css';
import './GetAiReport.css';
// Re-using the same CSS for all processing pages is efficient
import './ReportProcessing.css';
import './PremiumReportProcessing.css';


// Other Components
import Profile from './Profile';
import Counselling from './Counselling';
import MySession from './MySession';
import MyForm from './MyForm';
import ReportResultPage from "./ReportResultPage";
import MyReports from "./MyReports";
import GetAiReport from './GetAiReport';
import ReportProcessing from './ReportProcessing';
import PremiumReportProcessing from './PremiumReportProcessing';
import AIReportDisplay from './AIReportDisplay';
import AIReportDisplay2 from './AIReportDisplay2';
// ★★★ NEW IMPORTS ★★★
import AIReportPlusProcessing from './AIReportPlusProcessing'; // For the 249 package processing
import AIReportDisplay3 from './AIReportDisplay3'; // For displaying the 249 package report


// Icons
import {
  Home,
  TrendingUp,
  GraduationCap,
  Plane,
  User,
  Calendar,
  AlertTriangle,
  ChevronRight,
  BookOpen,
  Users,
  Menu,
  X,
  Loader2,
  FileText,
  CheckCircle
} from 'lucide-react';

const REACT_APP_API_BASE = process.env.REACT_APP_API_BASE;
const COUNSELLNG_API_BASE = process.env.REACT_APP_COUNSELLING_API_BASE;

// WordPress Category IDs from your blog page
const DEADLINE_CAT_ID = 2; // "Important Deadlines" category
const WHATS_NEW_CAT_ID = 3; // "What's New" category 

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const Dashboard = () => {
  const query = useQuery();
  const navigate = useNavigate();

  // ★★★ ADDED: State and effect to track mobile view ★★★
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    // Cleanup the event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // ★★★ START: ADDED SCRIPT FOR GOOGLE ADS CONVERSION ★★★
  useEffect(() => {
    const allowedDomains = ['getnextvisa.com', 'www.getnextvisa.com'];
    const currentHostname = window.location.hostname;

    // Only run the conversion script if the hostname matches our production domains.
    if (allowedDomains.includes(currentHostname)) {
      // Ensure the gtag function exists before calling it.
      if (typeof window.gtag === 'function') {
        console.log("Dashboard loaded on production. Firing Google Ads conversion event.");
        window.gtag('event', 'conversion', {
          'send_to': 'AW-16756404764/-v1iCNy54fwaEJzkibY-'
        });
      } else {
        console.error("window.gtag function not found on production domain. Conversion event not sent.");
      }
    } else {
      // This message is helpful for debugging on localhost or staging servers.
      console.log(`Skipping Google Ads conversion event on non-production domain: ${currentHostname}`);
    }
  }, []); // The empty dependency array `[]` ensures this effect runs only ONCE when the component mounts.
  // ★★★ END: ADDED SCRIPT ★★★

  // Tab + sidebar state
  const getInitialTab = () => query.get('view') || 'home';
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Meetings state
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [loadingMeetings, setLoadingMeetings] = useState(true);

  // Profile state
  const [profileData, setProfileData] = useState(null);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Blog posts state
  const [deadlinePost, setDeadlinePost] = useState(null);
  const [newsPost, setNewsPost] = useState(null);
  const [loadingBlogs, setLoadingBlogs] = useState(true);

  // ★★★ ADD THE NEW VIEWS TO THE FULL-WIDTH LAYOUT LIST ★★★
  const fullWidthTabs = [
    'reports',
    'get-ai-report',
    'report-processing',
    'report-premium-processing',
    'report-plus-processing',
    'report-result',
    'report-premium',
    'report-plus'
  ];
  const isFullWidthLayout = fullWidthTabs.includes(activeTab);


   const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);



  // Helper to compute profile %
  const calculateProfileCompletion = (data) => {
    if (!data) return 0;
    const fields = ['firstName', 'lastName', 'phone', 'dob', 'location', 'bio', 'avatarUrl'];
    const completed = fields.reduce((sum, f) => sum + (!!data[f] && data[f].toString().trim() !== '' ? 1 : 0), 0);
    return Math.round((completed / fields.length) * 100);
  };

  // Fetch meetings
  const fetchUpcomingMeetings = async () => {
    setLoadingMeetings(true);
    const userId = localStorage.getItem('userId')?.trim();
    if (!userId) {
      setUpcomingMeetings([]);
      setLoadingMeetings(false);
      return;
    }
    try {
      const { data } = await axios.get(`${COUNSELLNG_API_BASE}/api/sessions`, {
        params: { userId },
      });

      const now = new Date();

      const futureMeetings = data
        .filter(meeting => new Date(meeting.dateTime) > now)
        .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));

      setUpcomingMeetings(futureMeetings);

    } catch (err) {
      console.error("Failed to fetch upcoming meetings:", err);
      setUpcomingMeetings([]);
    } finally {
      setLoadingMeetings(false);
    }
  };

  // Fetch profile + blogs on mount
  useEffect(() => {
    // Profile
    const fetchProfile = async () => {
      setLoadingProfile(true);
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setLoadingProfile(false);
        return;
      }
      try {
        const { data } = await axios.get(`${REACT_APP_API_BASE}/api/user/${userId}`);
        setProfileData(data);
        setProfileCompletion(calculateProfileCompletion(data));
      } catch (err) {
        console.error("Failed to fetch profile:", err);
      } finally {
        setLoadingProfile(false);
      }
    };

    // Blogs
    const fetchBlogs = async () => {
      setLoadingBlogs(true);
      try {
        const [dlRes, nwRes] = await Promise.all([
          axios.get('https://getnextvisa.com/blogs/wp-json/wp/v2/posts', {
            params: { per_page: 1, categories: DEADLINE_CAT_ID, _embed: true },
          }),
          axios.get('https://getnextvisa.com/blogs/wp-json/wp/v2/posts', {
            params: { per_page: 1, categories: WHATS_NEW_CAT_ID, _embed: true },
          }),
        ]);

        setDeadlinePost(dlRes.data[0] || null);
        setNewsPost(nwRes.data[0] || null);
      } catch (err) {
        console.error("Failed to fetch blog posts:", err);
      } finally {
        setLoadingBlogs(false);
      }
    };

    fetchUpcomingMeetings();
    fetchProfile();
    fetchBlogs();
  }, []);

  // Sync URL → tab
  useEffect(() => {
    const view = query.get('view') || 'home';
    if (view !== activeTab) setActiveTab(view);
  }, [query, activeTab]);

  const sidebarItems = [
    { id: 'home', icon: Home, label: 'Dashboard' },
    { id: 'get-ai-report', icon: TrendingUp, label: 'Get AI Report' },
    { id: 'counselling', icon: GraduationCap, label: 'Book Session' },
    { id: 'reports', icon: FileText, label: 'My Reports' },
    { id: 'sessions', icon: Plane, label: 'My Sessions' },
    { id: 'form', icon: Users, label: 'My Form' },
  ];

  const toggleSidebar = () => setSidebarOpen(open => !open);
  const closeSidebar = () => setSidebarOpen(false);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    closeSidebar();
    navigate(tabId === 'home' ? '/dashboard' : `/dashboard?view=${tabId}`);
  };

 const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <Profile onProfileUpdate={() => window.location.reload()} />;
      case 'counselling':
        return <Counselling onBookingSuccess={fetchUpcomingMeetings} />;
      case 'sessions':
        return <MySession />;
      case 'form':
        return <MyForm />;
      case 'get-ai-report':
        return <GetAiReport />;
      case 'reports':
        return <MyReports key="reports" />;
      
      // ★★★ ROUTING FIXES APPLIED HERE ★★★

      // --- Base Report (e.g., ₹99) ---
      case 'report-processing':
        return <ReportProcessing />; // The processing page
      case 'report-result':
        return <AIReportDisplay />;  // The final report view

      // --- Premium Report (e.g., ₹149) ---
      case 'report-premium-processing':
        return <PremiumReportProcessing />; // The processing page
      case 'report-premium': 
        return <AIReportDisplay2 />;        // The final report view

      // --- Advance Report (e.g., ₹249) ---
      case 'report-plus-processing':
        return <AIReportPlusProcessing />; // The processing page
      case 'report-plus':
        return <AIReportDisplay3 />;       // The final report view

      case 'home':
      default:
        return (
          <>
            <Helmet>
              <meta name="title" content="Home | Get Next Visa - AI Powered Student Visa Report" />
              <title>Home | Get Next Visa - AI Powered Student Visa Report</title>
              <meta name="description" content="Get personalized, AI-powered visa reports. Discover the best countries and colleges for your study abroad journey." />
            </Helmet>

            {/* Header + Cards */}
            <div className="header-main-dashboard">
              <div className="header-text">
                <h1 className="welcome-title">
                  Welcome, {profileData?.firstName || 'User'}
                </h1>
                <p className="welcome-subtitle">
                  Begin your visa counselling journey with us!
                </p>
              </div>
            </div>

            <div className="action-cards">
              {/* Get AI Report */}
              <div className="action-card action-card-green">
                <div className="card-content">
                  <TrendingUp className="card-icon" />
                  <div>
                    <h3 className="card-title dashboard-cards">Get AI Report Now</h3>
                    <p className="card-subtitle">Get your report generated by AI</p>
                  </div>
                </div>
                <button className="card-button" onClick={() => handleTabClick('get-ai-report')}>
                  Get Report <ChevronRight className="button-arrow" />
                </button>
              </div>

              {/* My Reports */}
              <div className="action-card action-card-blue">
                <div className="card-content">
                  <FileText className="card-icon" />
                  <div>
                    <h3 className="card-title dashboard-cards">My Reports</h3>
                    <p className="card-subtitle">Access your generated AI reports</p>
                  </div>
                </div>
                <button className="card-button" onClick={() => handleTabClick('reports')}>
                  View Reports <ChevronRight className="button-arrow" />
                </button>
              </div>

              {/* Book Counselling */}
              <div className="action-card action-card-orange">
                <div className="card-content">
                  <BookOpen className="card-icon" />
                  <div>
                    <h3 className="card-title dashboard-cards">Book Counselling</h3>
                    <p className="card-subtitle">Session with our expert counsellors</p>
                  </div>
                </div>
                <button className="card-button" onClick={() => handleTabClick('counselling')}>
                  Book Now <ChevronRight className="button-arrow" />
                </button>
              </div>
            </div>

            {/* Important Deadlines */}
            <div className="deadlines-section">
              <div className="section-headerr">
                <div className="deadline-title">
                  <AlertTriangle className="alert-icon" />
                  <h3 className="title-main">Important Deadlines</h3>
                </div>
                <button
                  className="view-more-btn"
                  onClick={() => window.open('https://getnextvisa.com/blogs', '_blank')}
                >
                  View More
                </button>

              </div>

              {loadingBlogs ? (<p>Loading deadlines…</p>) : deadlinePost ? (
                <div className="deadline-item">
                  <h4 className="deadline-heading"> {deadlinePost.title.rendered} </h4>
                  <p className="deadline-text" dangerouslySetInnerHTML={{ __html: deadlinePost.excerpt.rendered.replace(/<a[\s\S]*?<\/a>/, '…') }} />
                  <div className="deadline-meta">
                    <span className="deadline-date"> {formatDistanceToNow(new Date(deadlinePost.date), { addSuffix: true })} </span>
                    <button
                      className="read-btn"
                      onClick={() => window.open(`https://getnextvisa.com/blogs/${deadlinePost.slug}`, '_blank')}
                    >
                      Read More
                    </button>

                  </div>
                </div>
              ) : (<p>No deadlines found.</p>)}
            </div>

            {/* What's New */}
            <div className="news-section">
              <div className="section-headerr">
                <h3 className="title-main">What's New?</h3>
              </div>

              {loadingBlogs ? (<p>Loading updates…</p>) : newsPost ? (
                <div className="news-item">
                  <div className="news-image">
                    {newsPost._embedded?.['wp:featuredmedia']?.[0]?.source_url && (
                      <img src={newsPost._embedded['wp:featuredmedia'][0].source_url} alt={newsPost.title.rendered} />
                    )}
                    <div className="news-badge">
                      <div className="news-badge-title">Result 2025</div>
                      <div className="news-badge-subtitle">UPDATES</div>
                    </div>
                  </div>
                  <div className="news-content">
                    <h4 className="news-title"> {newsPost.title.rendered} </h4>
                    <p className="news-text" dangerouslySetInnerHTML={{ __html: newsPost.excerpt.rendered.replace(/<a[\s\S]*?<\/a>/, '…') }} />
                    <div className="news-meta">
                      <span className="news-date"> {formatDistanceToNow(new
                        Date(newsPost.date), { addSuffix: true })} </span>
                      <button
                        className="read-more-btn"
                        onClick={() => window.open(`https://getnextvisa.com/blogs/${newsPost.slug}`, '_blank')}
                      >
                        Read More <ChevronRight className="button-arrow" />
                      </button>

                    </div>
                  </div>
                </div>
              ) : (<p>No updates found.</p>)}
            </div>
          </>
        );
    }
  };

  // ★★★ LOGIC TO DETERMINE IF THE RIGHT SIDEBAR SHOULD BE RENDERED ★★★
  const shouldShowRightSidebar = !isFullWidthLayout && (!isMobile || activeTab === 'home');


  return (
    <div className="dashboard-container">
      {/* Mobile menu */}
      <button className="mobile-menu-toggle" onClick={toggleSidebar}>
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar overlay */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'active' : ''}`}>
        <nav className="sidebar-nav">
          {sidebarItems.map(item => (
            <button
              key={item.id}
              onClick={() => handleTabClick(item.id)}
              className={`sidebar-item ${activeTab === item.id ? 'sidebar-item-active' : ''}`}
            >
              <item.icon className="sidebar-icon" />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Main layout */}
      <div className={`main-layout ${isFullWidthLayout ? 'full-width' : ''}`}>
        <div className="main-content">
          {renderContent()}
        </div>
      </div>

      {/* Right sidebar - Conditionally rendered based on the new logic */}
      {shouldShowRightSidebar && (
        <div className="right-sidebar">

          {/* --- CORRECTED LOGIC --- */}
          {/* Show this card on all tabs EXCEPT when the active tab is 'counselling' */}
          {activeTab !== 'counselling' && (
            <div className="sidebar-card hide-on-mobile">
              <h3 className="sidebar-card-title"> Abroad Counselling Session</h3>
              <p className="sidebar-card-text">
                Book a Free Abroad Counselling Session
              </p>
              <button className="book-now-btn" onClick={() => handleTabClick('counselling')}>
                Book Now
              </button>
            </div>
          )}

          {/* Upcoming meetings */}
          {activeTab !== 'sessions' && (
            <div className="sidebar-card">
              <h3 className="sidebar-card-title">Upcoming Meetings</h3>
              {loadingMeetings ? (
                <div className="meeting-loading"> <Loader2 className="animate-spin" /> <p>Loading meetings...</p> </div>
              ) : upcomingMeetings.length > 0 ? (
                <>
                  <div className="meeting-list">
                    {upcomingMeetings.slice(0, 2).map(meeting => (
                      <div key={meeting._id} className="meeting-item">
                        <p className="meeting-subject">{meeting.subject}</p>
                        <p className="meeting-datetime"> {format(new Date(meeting.dateTime.slice(0, -1)), "EEE, MMM d 'at' h:mmaa")} </p>
                      </div>
                    ))}
                  </div>
                  <button className="meetings-view-more-btn" onClick={() => handleTabClick('sessions')}>
                    View All Sessions
                  </button>
                </>
              ) : (
                <div className="empty-state">
                  <Calendar className="empty-icon" />
                  <p className="empty-title">No upcoming meetings</p>
                  <p className="empty-subtitle"> Book a session with our counsellors to get started </p>
                  <button className="empty-action" onClick={() => handleTabClick('counselling')}>
                    Book Your First Session
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Profile progress */}
          <div className="sidebar-card">
            <h3 className="sidebar-card-title">Profile Progress</h3>
            {loadingProfile ? (
              <div className="meeting-loading"> <Loader2 className="animate-spin" /> <p>Checking profile...</p> </div>
            ) : (
              <div className="profile-progress-content">
                <div className="profile-progress-info">
                  <p className="progress-percentage">{profileCompletion}% Complete</p>
                  <div className="progress-bar-container">
                    <div className="progress-bar-fill" style={{ width: `${profileCompletion}%` }} />
                  </div>
                </div>
                {profileCompletion < 100 ? (
                  <>
                    <p className="sidebar-card-text"> Complete your profile to get personalized recommendations. </p>
                    <button className="complete-profile-btn" onClick={() => handleTabClick('profile')}>
                      Complete Your Profile
                    </button>
                  </>
                ) : (
                  <div className="profile-complete-state"> <CheckCircle className="complete-icon" /> <p>Your profile is up to date. Well done!</p> </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;